import React from 'react';
import { motion } from 'framer-motion';
import { DownloadIcon, PrinterIcon, UsersIcon, FolderOpenIcon, SearchIcon } from 'lucide-react';
export function HowItWorks() {
  const steps = [{
    number: '01',
    icon: DownloadIcon,
    title: 'Purchase and download instantly',
    description: 'After checkout you receive your PDF files immediately. No waiting. No shipping.'
  }, {
    number: '02',
    icon: PrinterIcon,
    title: 'Print at home or read on screen',
    description: 'Print the pages or play directly from a tablet or laptop. Either way works perfectly.'
  }, {
    number: '03',
    icon: UsersIcon,
    title: 'Gather 2 to 6 players',
    description: 'Works for date night, friend night, or family night. The more minds, the better.'
  }, {
    number: '04',
    icon: FolderOpenIcon,
    title: 'Open the case. Start investigating.',
    description: "Each file includes everything you need — evidence, suspect profiles, timelines, clues and a sealed solution envelope you only open when you're ready."
  }, {
    number: '05',
    icon: SearchIcon,
    title: 'Can you solve it?',
    description: "That's the only question that matters tonight."
  }];
  return <section id="how-it-works" className="py-16 sm:py-24 lg:py-32 bg-[#0a0a0a] relative" data-id="element-214">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-215">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12 sm:mb-24" data-id="element-216">
          <h2 className="font-typewriter text-3xl sm:text-5xl lg:text-6xl text-[#ece3d0] mb-3 sm:mb-6 drop-shadow-lg" data-id="element-217">
            How it works
          </h2>
          <p className="font-serif-body text-[#ece3d0]/70 text-base sm:text-xl" data-id="element-218">
            From purchase to solving. Five simple steps.
          </p>
        </motion.div>

        <div className="relative" data-id="element-219">
          {/* The Red String (Spine) */}
          <div className="absolute left-8 sm:left-1/2 top-0 bottom-0 w-0.5 bg-[#8b1a1a] shadow-[0_0_8px_rgba(139,26,26,0.8)] sm:-translate-x-1/2" data-id="element-220" />

          <div className="space-y-8 sm:space-y-24" data-id="element-221">
            {steps.map((step, index) => {
            const isEven = index % 2 === 0;
            return <motion.div key={step.number} initial={{
              opacity: 0,
              y: 50
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-100px'
            }} transition={{
              duration: 0.8,
              ease: [0.16, 1, 0.3, 1]
            }} className={`relative flex items-center ${isEven ? 'sm:flex-row-reverse' : 'sm:flex-row'} pl-20 sm:pl-0`} data-id="element-222">
                  {/* Node on the string */}
                  <div className="absolute left-8 sm:left-1/2 w-4 h-4 rounded-full bg-[#ece3d0] border-4 border-[#8b1a1a] -translate-x-1/2 shadow-[0_0_10px_rgba(236,227,208,0.5)] z-10" data-id="element-223" />

                  {/* Evidence Tag Card */}
                  <div className={`w-full sm:w-[45%] ${isEven ? 'sm:pl-12' : 'sm:pr-12'}`} data-id="element-224">
                    <div className="bg-[#ece3d0] bg-paper-texture rounded p-5 sm:p-8 shadow-xl border border-[#d4c5b0] relative group hover:-translate-y-1 transition-transform duration-300" data-id="element-225">
                      {/* Tag Hole & String detail */}
                      <div className={`hidden sm:block absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-[#0a0a0a] border border-[#d4c5b0] shadow-inner ${isEven ? '-left-2' : '-right-2'}`} data-id="element-226" />
                      <div className={`hidden sm:block absolute top-1/2 -translate-y-1/2 w-12 h-0.5 bg-[#8b1a1a]/50 ${isEven ? '-left-12' : '-right-12'}`} data-id="element-227" />

                      <div className="flex items-center space-x-3 sm:space-x-4 mb-3 sm:mb-4" data-id="element-228">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#1a1a1a] rounded flex items-center justify-center shrink-0" data-id="element-229">
                          <step.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#ece3d0]" data-id="element-230" />
                        </div>
                        <span className="font-typewriter text-[#8b1a1a] text-xl sm:text-2xl font-bold" data-id="element-231">
                          {step.number}
                        </span>
                      </div>

                      <h3 className="font-typewriter text-lg sm:text-2xl text-[#1a1a1a] mb-2 sm:mb-3 font-bold leading-tight" data-id="element-232">
                        {step.title}
                      </h3>
                      <p className="font-serif-body text-[#2a2a2a] text-base sm:text-lg leading-relaxed" data-id="element-233">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}